import "@fortawesome/fontawesome-svg-core/styles.css";
import "./style.css";

import { all } from "@awesome.me/kit-698d8619ec/icons";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
import { defaultConfig as formkitDefaultConfig, plugin as formkit } from "@formkit/vue";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { Settings } from "luxon";
import { createPinia } from "pinia";
import { createPersistedState } from "pinia-plugin-persistedstate";
import { createApp } from "vue";
import { createI18n } from "vue-i18n";

import formkitConfig from "./formkit.config.js";
import messages from "@intlify/unplugin-vue-i18n/messages";

import App from "./App.vue";

import { useAuthStore } from "./pinia/auth";
import { resetAndClearPersistedPlugin } from "./pinia/plugins/reset-persisted-state.js";
import { router } from "./routes-user.ts";
import { installResponseInterceptors } from "./services/responseInterceptors.ts";
import { getClientFingerprint } from "./services/fingerprint";
import { isNativePlatform } from "./capacitor/platform";
import { ensureIpChangeStarted } from "./capacitor/server-url";
import { initAppUpdater } from "./capacitor/app-updater";
import { initAuthCallback } from "./capacitor/auth-callback";
import {
  initBatteryOptimizationWatcher,
  restorePendingBatteryOptimizationPrompt,
} from "./capacitor/battery-optimization";
import { getSessionToken } from "./capacitor/session-token";
import { initExpiryNotifications } from "./capacitor/expiry-notifications";
import { initForceLogoutWatcher } from "./capacitor/force-logout";
import { initNotificationPermissionWatcher } from "./capacitor/notification-permissions";
import { initServiceHealthWatcher } from "./capacitor/service-health";

Settings.defaultLocale = Intl.DateTimeFormat(window.navigator.languages).resolvedOptions().locale;

const i18n = createI18n({
  legacy: false,
  locale: navigator.language,
  fallbackLocale: "en-AU",
  messages,
});

library.add(...all);

const storage = sessionStorage;

const pinia = createPinia();
pinia.use(
  createPersistedState({
    storage: storage,
  }),
);

pinia.use(resetAndClearPersistedPlugin(storage));

const app = createApp(App)
  .use(router)
  .use(pinia)
  .use(i18n)
  .use(autoAnimatePlugin)
  .use(formkit, formkitDefaultConfig(formkitConfig))
  .component("font-awesome-icon", FontAwesomeIcon);

// Wire response policy after pinia is active so the store is resolvable. On a
// 401 the interceptor tears down this portal's cached session before redirecting.
installResponseInterceptors(router, useAuthStore());

// Warm the client fingerprint so it's usually ready before the first API call.
// Fire-and-forget; the request interceptor still awaits the cached promise.
void getClientFingerprint();

if (isNativePlatform()) {
  document.body.classList.add("native-app");
}

app.mount("#app");

if (isNativePlatform()) {
  const forceLogoutHandler = async () => {
    try {
      const authStore = useAuthStore();
      await authStore.logout();
    } catch (e) {
      console.warn("[main-user] force logout: authStore.logout failed:", e);
    }
    await router.push({ name: "Login" });
  };

  initAuthCallback(router);
  initNotificationPermissionWatcher();
  initExpiryNotifications();
  ensureIpChangeStarted();
  initAppUpdater();

  // Android-only: OEM battery-optimisation prompt, foreground-service-killed
  // banner, and force-logout flag from the FGS notification action.
  initBatteryOptimizationWatcher();
  initServiceHealthWatcher();
  initForceLogoutWatcher(forceLogoutHandler);

  // Login surfaces the battery-optimisation overlay, then postLoginNavigate
  // does a full-page reload that wipes it from memory. Re-surface it here when
  // a session is present, so it survives that one reload.
  getSessionToken().then((token) => {
    if (token) restorePendingBatteryOptimizationPrompt();
  });
}
